import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  Api,
  ApiConfiguration,
  createDocument,
  createDocumentClarification,
  exportDocumentMonth,
  getDocument,
  getDocumentMonths,
  getDocumentViewUrl,
  getDocuments,
  getProviders,
  removeDocument,
  updateDocumentBusinessNote,
} from '@taxora-api';
import {
  DocumentMonthSummaryDto,
  DocumentResponseDto,
  DocumentsListResponseDto,
  MissingDocumentRequestDto,
} from '@taxora-shared-common/util';
import {
  BusinessCloseDrawer,
  BusinessCreateClarification,
  BusinessDownloadMonth,
  BusinessExportMonth,
  BusinessLoadDocument,
  BusinessLoadDocuments,
  BusinessLoadMonths,
  BusinessMarkMissingDocumentRequestSubmitted,
  BusinessOpenDrawer,
  BusinessResetDocuments,
  CreateDocument,
  RemoveDocument,
  UpdateBusinessNote,
} from './documents.actions';

const VIEW_URL_TTL_MS = 50 * 60 * 1000;

const ACTIVE_MISSING_DOCUMENT_REQUEST_STATUSES = ['PENDING', 'SUBMITTED', 'UNDER_REVIEW'];

interface CachedViewUrl {
  url: string;
  cachedAt: number;
}

function monthKey(year: number | null | undefined, month: number | null | undefined): string {
  return `${year ?? 'null'}-${month ?? 'null'}`;
}

interface BusinessDocumentsStateModel {
  months: DocumentMonthSummaryDto[];
  documentsByMonth: Record<string, DocumentsListResponseDto>;
  missingDocumentRequests: MissingDocumentRequestDto[];
  loadingMonths: Record<string, boolean>;
  errorMonths: Record<string, boolean>;
  selectedDocument: DocumentResponseDto | null;
  drawerOpen: boolean;
  isLoadingMonths: boolean;
  isLoadingDocument: boolean;
  isUploading: boolean;
  isRemoving: boolean;
  isSendingClarification: boolean;
  isUpdatingNote: boolean;
  uploadError: string | null;
  providerRelationshipId: number | null;
}

@State<BusinessDocumentsStateModel>({
  name: 'businessDocuments',
  defaults: {
    months: [],
    documentsByMonth: {},
    missingDocumentRequests: [],
    loadingMonths: {},
    errorMonths: {},
    selectedDocument: null,
    drawerOpen: false,
    isLoadingMonths: false,
    isLoadingDocument: false,
    isUploading: false,
    isRemoving: false,
    isSendingClarification: false,
    isUpdatingNote: false,
    uploadError: null,
    providerRelationshipId: null,
  },
})
@Injectable()
export class BusinessDocumentsState {
  private readonly api = inject(Api);
  private readonly apiConfig = inject(ApiConfiguration);

  private viewUrlCache = new Map<number, CachedViewUrl>();

  @Selector()
  static months(state: BusinessDocumentsStateModel): DocumentMonthSummaryDto[] {
    return state.months;
  }

  @Selector()
  static documentsByMonth(
    state: BusinessDocumentsStateModel,
  ): Record<string, DocumentsListResponseDto> {
    return state.documentsByMonth;
  }

  @Selector()
  static missingDocumentRequests(state: BusinessDocumentsStateModel): MissingDocumentRequestDto[] {
    return state.missingDocumentRequests;
  }

  @Selector()
  static activeMissingDocumentRequests(
    state: BusinessDocumentsStateModel,
  ): MissingDocumentRequestDto[] {
    return state.missingDocumentRequests.filter((request) =>
      ACTIVE_MISSING_DOCUMENT_REQUEST_STATUSES.includes(request.status),
    );
  }

  @Selector()
  static needsAttentionSummary(state: BusinessDocumentsStateModel): {
    clarificationDocuments: number;
    activeMissingRequests: number;
    total: number;
  } {
    // Sourced from the per-month aggregate the backend already returns with
    // `LoadMonths`, not from `documentsByMonth` — that map is only populated
    // for months the user has actually expanded, so deriving this from it
    // silently under-counts (typically reports 0) on every fresh page load.
    const clarificationDocuments = state.months.reduce(
      (total, month) => total + (month.needsClarificationCount ?? 0),
      0,
    );
    const activeMissingRequests = state.missingDocumentRequests.filter((request) =>
      ACTIVE_MISSING_DOCUMENT_REQUEST_STATUSES.includes(request.status),
    ).length;

    return {
      clarificationDocuments,
      activeMissingRequests,
      total: clarificationDocuments + activeMissingRequests,
    };
  }

  @Selector()
  static selectedDocument(state: BusinessDocumentsStateModel): DocumentResponseDto | null {
    return state.selectedDocument;
  }

  @Selector()
  static drawerOpen(state: BusinessDocumentsStateModel): boolean {
    return state.drawerOpen;
  }

  @Selector()
  static isLoadingMonths(state: BusinessDocumentsStateModel): boolean {
    return state.isLoadingMonths;
  }

  @Selector()
  static isLoadingDocument(state: BusinessDocumentsStateModel): boolean {
    return state.isLoadingDocument;
  }

  @Selector()
  static isUploading(state: BusinessDocumentsStateModel): boolean {
    return state.isUploading;
  }

  @Selector()
  static isRemoving(state: BusinessDocumentsStateModel): boolean {
    return state.isRemoving;
  }

  @Selector()
  static isSendingClarification(state: BusinessDocumentsStateModel): boolean {
    return state.isSendingClarification;
  }

  @Selector()
  static isUpdatingNote(state: BusinessDocumentsStateModel): boolean {
    return state.isUpdatingNote;
  }

  @Selector()
  static uploadError(state: BusinessDocumentsStateModel): string | null {
    return state.uploadError;
  }

  @Selector()
  static loadingMonths(state: BusinessDocumentsStateModel): Record<string, boolean> {
    return state.loadingMonths;
  }

  @Selector()
  static providerRelationshipId(state: BusinessDocumentsStateModel): number | null {
    return state.providerRelationshipId;
  }

  @Action(BusinessLoadMonths)
  async loadMonths(
    { getState, patchState }: StateContext<BusinessDocumentsStateModel>,
    { relationshipId, organizationId }: BusinessLoadMonths,
  ): Promise<void> {
    patchState({ isLoadingMonths: true });
    try {
      let resolvedId = relationshipId || getState().providerRelationshipId;
      if (!resolvedId) {
        // Scoped to ACTIVE only — an organization can accumulate INACTIVE/past
        // provider relationships over time (terminated collaborations), and an
        // unfiltered list is ordered newest-first, so picking `data[0]` could
        // silently resolve to a defunct relationship instead of the current one.
        const providers = await this.api.invoke(getProviders, {
          organizationId,
          status: 'ACTIVE',
        });
        resolvedId = providers.data[0]?.id ?? null;
        if (resolvedId) patchState({ providerRelationshipId: resolvedId });
      }
      if (!resolvedId) return;
      const months = await this.api.invoke(getDocumentMonths, {
        relationshipId: resolvedId,
        organizationId,
      });
      patchState({ months: months as unknown as DocumentMonthSummaryDto[] });
    } catch (error) {
      console.error('Failed to load document months', error);
    } finally {
      patchState({ isLoadingMonths: false });
    }
  }

  @Action(BusinessLoadDocuments)
  async loadDocuments(
    { getState, patchState }: StateContext<BusinessDocumentsStateModel>,
    { relationshipId, organizationId, year, month, filters }: BusinessLoadDocuments,
  ): Promise<void> {
    const resolvedId = relationshipId || getState().providerRelationshipId || 0;
    const key = monthKey(year, month);
    patchState({ loadingMonths: { ...getState().loadingMonths, [key]: true } });
    try {
      const result = await this.api.invoke(getDocuments, {
        relationshipId: resolvedId,
        organizationId,
        year: year ?? undefined,
        month: month ?? undefined,
        // Backend filters `documentDate IS NULL` server-side for this bucket — relying on
        // client-side filtering of the default (paginated, unfiltered) page silently missed
        // no-date documents once the relationship had more dated documents than fit on one page.
        noDocumentDate: year == null || month == null ? true : undefined,
        ...filters,
      });
      patchState({
        documentsByMonth: {
          ...getState().documentsByMonth,
          [key]: result as unknown as DocumentsListResponseDto,
        },
        loadingMonths: { ...getState().loadingMonths, [key]: false },
      });
    } catch (error) {
      console.error('Failed to load documents', error);
      patchState({
        errorMonths: { ...getState().errorMonths, [key]: true },
        loadingMonths: { ...getState().loadingMonths, [key]: false },
      });
    }
  }

  @Action(BusinessLoadDocument)
  async loadDocument(
    { patchState }: StateContext<BusinessDocumentsStateModel>,
    { id }: BusinessLoadDocument,
  ): Promise<void> {
    patchState({ isLoadingDocument: true });
    try {
      const selectedDocument = await this.api.invoke(getDocument, { id });
      patchState({ selectedDocument: selectedDocument as unknown as DocumentResponseDto });
    } catch (error) {
      console.error('Failed to load document', error);
    } finally {
      patchState({ isLoadingDocument: false });
    }
  }

  @Action(CreateDocument)
  async createDocument(
    { getState, patchState }: StateContext<BusinessDocumentsStateModel>,
    { payload }: CreateDocument,
  ): Promise<void> {
    patchState({ isUploading: true, uploadError: null });
    try {
      const document = await this.api.invoke(createDocument, {
        body: {
          file: payload.file,
          organizationRelationshipId: payload.organizationRelationshipId,
          type: payload.type,
          documentDate: payload.documentDate,
          reference: payload.reference,
          supplierName: payload.supplierName,
          supplierCui: payload.supplierCui,
          value: payload.value,
          currency: payload.currency,
          businessNote: payload.businessNote,
        },
      });
      const localDoc = document as unknown as DocumentResponseDto;
      const documentDate = localDoc.documentDate ?? payload.documentDate ?? null;
      const docYear = documentDate ? new Date(documentDate).getFullYear() : null;
      const docMonth = documentDate ? new Date(documentDate).getMonth() + 1 : null;
      const key = monthKey(docYear, docMonth);
      const state = getState();
      const currentEntry = state.documentsByMonth[key];
      const currentData = currentEntry?.data ?? [];
      const existingMonth = state.months.find((m) => m.year === docYear && m.month === docMonth);
      patchState({
        documentsByMonth: {
          ...state.documentsByMonth,
          [key]: {
            ...(currentEntry ?? { page: 1, limit: 50 }),
            data: [localDoc, ...currentData],
            total: (currentEntry?.total ?? currentData.length) + 1,
          },
        },
        months: existingMonth
          ? state.months.map((m) =>
              m.year === docYear && m.month === docMonth
                ? {
                    ...m,
                    count: m.count + 1,
                    totalAmount: (m.totalAmount ?? 0) + (document.value ?? 0),
                  }
                : m,
            )
          : [
              {
                year: docYear,
                month: docMonth,
                count: 1,
                label: documentDate
                  ? new Date(documentDate).toLocaleString('ro', {
                      month: 'long',
                      year: 'numeric',
                    })
                  : 'Fara data document',
                totalAmount: document.value ?? 0,
                currency: document.currency ?? 'RON',
              },
              ...state.months,
            ],
      });
    } catch (error) {
      console.error('Failed to create document', error);
      patchState({ uploadError: 'A apărut o eroare la încărcarea documentului.' });
    } finally {
      patchState({ isUploading: false });
    }
  }

  @Action(RemoveDocument)
  async removeDocument(
    { getState, patchState }: StateContext<BusinessDocumentsStateModel>,
    { id, reason }: RemoveDocument,
  ): Promise<void> {
    patchState({ isRemoving: true });
    try {
      await this.api.invoke(removeDocument, { id, body: { reason } });
      const state = getState();
      const documentsByMonth: Record<string, DocumentsListResponseDto> = {};
      for (const [key, entry] of Object.entries(state.documentsByMonth)) {
        const filtered = entry.data.filter((d) => d.id !== id);
        documentsByMonth[key] = { ...entry, data: filtered, total: filtered.length };
      }
      const months = state.months.map((m) => {
        const key = monthKey(m.year, m.month);
        const entry = documentsByMonth[key];
        return entry ? { ...m, count: entry.total } : m;
      });
      patchState({ documentsByMonth, months, selectedDocument: null, drawerOpen: false });
    } catch (error) {
      console.error('Failed to remove document', error);
      throw error;
    } finally {
      patchState({ isRemoving: false });
    }
  }

  @Action(BusinessCreateClarification)
  async createClarification(
    { patchState }: StateContext<BusinessDocumentsStateModel>,
    { id, message }: BusinessCreateClarification,
  ): Promise<void> {
    patchState({ isSendingClarification: true });
    try {
      const updated = await this.api.invoke(createDocumentClarification, { id, body: { message } });
      patchState({ selectedDocument: updated as unknown as DocumentResponseDto });
    } catch (error) {
      console.error('Failed to create clarification', error);
      throw error;
    } finally {
      patchState({ isSendingClarification: false });
    }
  }

  @Action(UpdateBusinessNote)
  async updateBusinessNote(
    { patchState }: StateContext<BusinessDocumentsStateModel>,
    { id, note }: UpdateBusinessNote,
  ): Promise<void> {
    patchState({ isUpdatingNote: true });
    try {
      const updated = await this.api.invoke(updateDocumentBusinessNote, { id, body: { note } });
      patchState({ selectedDocument: updated as unknown as DocumentResponseDto });
    } catch (error) {
      console.error('Failed to update business note', error);
      throw error;
    } finally {
      patchState({ isUpdatingNote: false });
    }
  }

  @Action(BusinessExportMonth)
  async exportMonth(
    _ctx: StateContext<BusinessDocumentsStateModel>,
    { month, relationshipId, organizationId }: BusinessExportMonth,
  ): Promise<void> {
    if (month.year == null || month.month == null) return;
    await this.api.invoke(exportDocumentMonth, {
      body: { relationshipId, organizationId, year: month.year, month: month.month },
    });
  }

  @Action(BusinessDownloadMonth)
  downloadMonth(
    _ctx: StateContext<BusinessDocumentsStateModel>,
    { month, relationshipId, organizationId }: BusinessDownloadMonth,
  ): void {
    if (month.year == null || month.month == null) return;
    const params = new URLSearchParams({
      relationshipId: String(relationshipId),
      organizationId: String(organizationId),
      year: String(month.year),
      month: String(month.month),
    });
    window.open(`${this.apiConfig.rootUrl}/documents/export/download?${params.toString()}`);
  }

  @Action(BusinessMarkMissingDocumentRequestSubmitted)
  markMissingDocumentRequestSubmitted(
    { getState, patchState }: StateContext<BusinessDocumentsStateModel>,
    { id }: BusinessMarkMissingDocumentRequestSubmitted,
  ): void {
    // TODO: replace with backend state transition once Missing Document endpoints are available.
    patchState({
      missingDocumentRequests: getState().missingDocumentRequests.map((request) =>
        request.id === id ? { ...request, status: 'SUBMITTED' } : request,
      ),
    });
  }

  @Action(BusinessOpenDrawer)
  openDrawer(
    { patchState, dispatch }: StateContext<BusinessDocumentsStateModel>,
    { id }: BusinessOpenDrawer,
  ): void {
    patchState({ drawerOpen: true });
    void dispatch(new BusinessLoadDocument(id));
  }

  @Action(BusinessCloseDrawer)
  closeDrawer({ patchState }: StateContext<BusinessDocumentsStateModel>): void {
    patchState({ drawerOpen: false, selectedDocument: null });
  }

  @Action(BusinessResetDocuments)
  reset({ setState }: StateContext<BusinessDocumentsStateModel>): void {
    this.viewUrlCache.clear();
    setState({
      months: [],
      documentsByMonth: {},
      missingDocumentRequests: [],
      loadingMonths: {},
      errorMonths: {},
      selectedDocument: null,
      drawerOpen: false,
      isLoadingMonths: false,
      isLoadingDocument: false,
      isUploading: false,
      isRemoving: false,
      isSendingClarification: false,
      isUpdatingNote: false,
      uploadError: null,
      providerRelationshipId: null,
    });
  }

  getCachedViewUrl(id: number): string | null {
    const cached = this.viewUrlCache.get(id);
    if (!cached) return null;
    if (Date.now() - cached.cachedAt > VIEW_URL_TTL_MS) {
      this.viewUrlCache.delete(id);
      return null;
    }
    return cached.url;
  }

  setCachedViewUrl(id: number, url: string): void {
    this.viewUrlCache.set(id, { url, cachedAt: Date.now() });
  }

  async getDocumentViewUrl(id: number): Promise<string | null> {
    const cached = this.getCachedViewUrl(id);
    if (cached) return cached;

    const response = await this.api.invoke(getDocumentViewUrl, { id });
    const url = response.url ?? null;
    if (url) {
      this.setCachedViewUrl(id, url);
    }
    return url;
  }
}
